import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, Observable, switchMap } from 'rxjs';
import { AppConfig } from 'src/app/app-config.service';
import { ToolService } from 'src/app/features/tooling/services/tool.service';

@Injectable({
    providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {

    isRefreshing = false;
    expiration = (new Date(0)).toISOString();
    token = '';
    private refreshBuffer = 5 * 60 * 1000; // Refresh 5 minutes before expiration

    constructor(private toolService: ToolService) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Check if the request is for the CDN; if not, pass it to the next handler
        if (!req.url.includes(`${AppConfig.settings.cdnUrl}`)) {
            return next.handle(req); // Pass the request to the next handler
        }
        else{
            // Wait if the token is being refreshed
            if (this.isRefreshing) {
                return new Observable<HttpEvent<any>>(observer => {
                    const interval = setInterval(() => {
                        if (!this.isRefreshing) {
                            clearInterval(interval);
                            observer.next(null as any);
                        }
                    }, 100);
                }).pipe(
                    switchMap(() => this.intercept(req, next)) // Retry the request after the token is refreshed
                );
            }
        
            // Check if the token is expired or will expire soon (proactive refresh)
            const now = new Date();
            const expirationDate = new Date(this.expiration);
            const shouldRefresh = (expirationDate.getTime() - now.getTime()) < this.refreshBuffer;
            
            if (shouldRefresh) {
                this.isRefreshing = true;
                return this.toolService.getImageStorageAccountTokenAsync().pipe(
                    switchMap(response => {
                        // Clone the request and add the new token
                        const clonedRequest = req.clone({
                            setHeaders: {
                                Authorization: `Bearer ${response.accessToken}`,
                                'x-ms-version': '2025-05-05'
                            }
                        });
    
                        this.expiration = response.expiration;
                        this.token = response.accessToken;
                        this.isRefreshing = false;
    
                        return next.handle(clonedRequest); // Pass the cloned request to the next handler
                    }),
                    catchError(error => {
                        this.isRefreshing = false;
                        this.expiration = (new Date(0)).toISOString();
                        throw error;
                    })
                );
            } else {
                // Clone the request and add the existing token
                const newReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${this.token}`,
                        'x-ms-version': '2025-05-05'
                    }
                });
                return next.handle(newReq); // Pass the cloned request to the next handler
            }
        }
    }
}